// ----------------------------------------------------------------------
// <copyright file="Izenda.RenderApi.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------
!(function() {
  var React = require('react');
  var ReactDOM = require('react-dom');
  var compose = require('recompose').compose;

  var renderAction = compose(
    require('../pages/Izenda.IntegrationStyleAction').defineContext,
    require('../pages/Izenda.ExposeMethodAction').defineContext
  );

  var App = renderAction(require('../Izenda.App'));
  var ReportPartPage = require('../pages/Izenda.ReportPartPage');
  var SettingPage = require('../pages/Izenda.SettingPage');
  var ReportPage = require('../pages/Izenda.ReportPage');
  var DashboardPage = renderAction(require('../pages/Izenda.DashboardPage'));
  var DashboardViewerPage = renderAction(require('../pages/Izenda.DashboardViewerPage'));
  var ReportViewerPage = renderAction(require('../pages/Izenda.ReportViewerPage'));
  var ReportDesignerPage = require('../pages/Izenda.ReportDesignerPage');
  var m = require('mobx');
  var IntergrateStore = require('share/utils/Izenda.IntergrateStore');

  const { forEach, cloneDeep } = require('lodash/fp');

  var addDestroy = function(container, resultApi) {
    resultApi.destroy = function() {
      ReactDOM.unmountComponentAtNode(container);
    };
  };

  var buildExposeFunction = function(container) {
    var returnObject = {};
    var registerExposeMethod = function(exposeMethod) {
      Object.assign(returnObject, exposeMethod);
    };
    var unregisterExposeMethod = function(methodNames) {
      forEach(function(methodName) {
        delete returnObject[methodName];
      }, methodNames);
    };

    addDestroy(container, returnObject);

    return {
      returnObject: returnObject,
      registerExposeMethod: registerExposeMethod,
      unregisterExposeMethod: unregisterExposeMethod
    };
  };

  module.exports = {
    /**
     * React BI Components
     */
    Components: {
      App: App,
      Settings: SettingPage,
      ReportDesigner: ReportDesignerPage,
      ReportViewer: ReportViewerPage,
      ReportPart: ReportPartPage,
      Report: ReportPage,
      Dashboard: DashboardPage,
      DashboardViewer: DashboardViewerPage
    },

    /**
     * Expose ReactDOM Component at node to use the same version of ReactDOM for
     * both render and unmountComponentAtNode
     * @method unmountComponent
     * @param  {DOM} container
     */
    unmountComponent: function unmountComponent(container) {
      IntergrateStore.reset();
      ReactDOM.unmountComponentAtNode(container);
    },

    /**
     * Render Synergy root component to actual DOM element.
     */
    render: function render(container, integrationStyle) {
      var exposeObject = buildExposeFunction(container);

      integrationStyle = m.observable(cloneDeep(integrationStyle));

      ReactDOM.render(
        <App
          registerExposeMethod={exposeObject.registerExposeMethod}
          unregisterExposeMethod={exposeObject.unregisterExposeMethod}
          integrationStyle={integrationStyle}
        />,
        container
      );

      return exposeObject.returnObject;
    },

    /**
     * This component is intended to show only report part content.
     * params:
       * id: required. Id of report part.
       * filter: is an array of Object with key and	value for filter on subreport
       * crosssfilters: is an array of Object with key and value for filter on report
       * overridingFilterValue: to override default fillter values
       * onPublishDrillInfo: this is a function which will be invoked
       *                   when drill on a cross filtering.
       *                   Cross filtering's filter info is passed as an array of {key:'key', value:'value'}
       *
       * for Example:
       *  "id": "554D4D2F-E12A-4680-A2EC-A405DE872B7F",
       *  "filters": [
                    {
                      key: "[northwind].[dbo].[Order Details].[ProductID]",
                      value: 5
                    }
                   ]
        *  "overridingFilterValue": {
             p1value:A,
             p2value:B,
           },
        *  "crosssfilters": [
                    {
                      key: "[northwind].[dbo].[Order].[OrderID]",
                      value: 10
                    }
                   ]
           onPublishDrillInfo: function(drillInfo) { // do something }

      * container: Dom element.
     */
    renderReportPart: function render(container, params) {
      const drillParts = (newParams, excludedPartId, fromOutside) => {
        const isCachedCrossFilteringPart = IntergrateStore.isCachedCrossFilteringPart(newParams.id);
        if (isCachedCrossFilteringPart) {
          IntergrateStore.addFilters(newParams.crosssfilters);
          !fromOutside && IntergrateStore.cleanFilter();
        }
        const parts = IntergrateStore.loadUpdatingReportParts(newParams.id, excludedPartId);

        forEach(part => {
          part.params.crosssfilters = isCachedCrossFilteringPart
            ? IntergrateStore.crossFilteringFilters
            : newParams.crosssfilters;
          newParams.filters && (part.params.filters = newParams.filters);
          newParams.overridingFilterValue && (part.params.overridingFilterValue = newParams.overridingFilterValue);
          part.partOptions.updateAlone(part.params);
        }, parts);
      };

      const onPublishDrillInfoProgress = (id, filters) => {
        drillParts({ id: id, crosssfilters: filters }, id);
        !!params.onPublishDrillInfo && params.onPublishDrillInfo(filters);
      };

      params.publishDrillInfo = onPublishDrillInfoProgress;
      ReactDOM.render(<ReportPartPage params={params} />, container);

      const partOptions = {
        updateResults: function(newParams) {
          drillParts(newParams, null, true);
        },
        updateAlone: function(newParams) {
          ReactDOM.render(<ReportPartPage params={newParams} />, container);
        }
      };
      IntergrateStore.addPart({
        id: params.id,
        partOptions: partOptions,
        params: params,
        reportPart: null
      });
      return partOptions;
    },

    /**
     * This component is intended to show only setting module.
     * container: Dom element.
     */
    renderSettingPage: function render(container) {
      ReactDOM.render(<SettingPage />, container);
    },

    /**
     * This component is intended to show only report module.
     * container: Dom element.
     */
    renderReportPage: function render(container, integrationStyle) {
      var exposeObject = buildExposeFunction(container);

      integrationStyle = m.observable(cloneDeep(integrationStyle));

      ReactDOM.render(
        <ReportPage
          registerExposeMethod={exposeObject.registerExposeMethod}
          unregisterExposeMethod={exposeObject.unregisterExposeMethod}
          integrationStyle={integrationStyle}
        />,
        container
      );

      return exposeObject.returnObject;
    },

    /**
      * This component is intended to show only report viewer.
       * container: Dom element.
       * reportId: GUID id of report
       * filter: object filter
       * 	ex:
       * 		{
              masterReportId: '',
              inheritMasterReportFilter : true/false,
              filters: [{
                // filter based on master report field mapping
                masterReportId: '',
                // master report part id
                reportPartId: '',
                // container name
                containerName: '',
                // field mapping 2 of item with position 1 in container
                key: 'fm1_2',
                value: 'a',
                originalValue: 'b',
                dataFormat: 'c',
                linkReportFilter: true,
            	Type: 'fieldMapping'
              },
              {
                // filter based on report part element alias
                key: fieldNameAlias,
                value: 'a',
            	Type: 'fieldNameAlias'
              }],
              overridingFilterValue:{
                // overide value of filter 1 in sub report
                // p(\d+)value : p1

                p1value: 'a',
                // overide value of filter 2 in master report
                // mp(\d+)value
                mp2value: 'b'
              }
            }
      */
    renderReportViewerPage: function render(container, reportId, filter, integrationStyle) {
      var exposeObject = buildExposeFunction(container);

      integrationStyle = m.observable(cloneDeep(integrationStyle));

      ReactDOM.render(
        <ReportViewerPage
          reportId={reportId}
          filter={filter}
          registerExposeMethod={exposeObject.registerExposeMethod}
          unregisterExposeMethod={exposeObject.unregisterExposeMethod}
          integrationStyle={integrationStyle}
        />,
        container
      );
      return exposeObject.returnObject;
    },

    /**
     * This component is intended to show dashboard.
     * container: Dom element.
     */
    renderDashboardPage: function render(container, integrationStyle) {
      var exposeObject = buildExposeFunction(container);

      integrationStyle = m.observable(cloneDeep(integrationStyle));

      ReactDOM.render(
        <DashboardPage
          registerExposeMethod={exposeObject.registerExposeMethod}
          unregisterExposeMethod={exposeObject.unregisterExposeMethod}
          integrationStyle={integrationStyle}
        />,
        container
      );

      return exposeObject.returnObject;
    },

    renderDashboardViewerPage: function render(container, dashboardId, filter, integrationStyle) {
      var exposeObject = buildExposeFunction(container);

      integrationStyle = m.observable(cloneDeep(integrationStyle));

      ReactDOM.render(
        <DashboardViewerPage
          dashboardId={dashboardId}
          filter={filter}
          registerExposeMethod={exposeObject.registerExposeMethod}
          unregisterExposeMethod={exposeObject.unregisterExposeMethod}
          integrationStyle={integrationStyle}
        />,
        container
      );

      return exposeObject.returnObject;
    },

    /**
     * This component is intended to show Report Designer Page.
     * container: Dom element.
     */
    renderReportDesignerPage: function render(container, integrationStyle) {
      var exposeObject = buildExposeFunction(container);

      integrationStyle = m.observable(cloneDeep(integrationStyle));

      ReactDOM.render(
        <ReportDesignerPage
          registerExposeMethod={exposeObject.registerExposeMethod}
          unregisterExposeMethod={exposeObject.unregisterExposeMethod}
          integrationStyle={integrationStyle}
        />,
        container
      );

      return exposeObject.returnObject;
    },

    /**
     * This component is intended to show new dashboard Page.
     * container: Dom element.
     */
    renderNewDashboardPage: function render(container, integrationStyle) {
      var exposeObject = buildExposeFunction(container);

      integrationStyle = m.observable(cloneDeep(integrationStyle));

      ReactDOM.render(
        <DashboardPage
          registerExposeMethod={exposeObject.registerExposeMethod}
          unregisterExposeMethod={exposeObject.unregisterExposeMethod}
          integrationStyle={integrationStyle}
          isAddNew
        />,
        container
      );

      return exposeObject.returnObject;
    }
  };
})();



// WEBPACK FOOTER //
// ./ui/api/Izenda.RenderApi.js